import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import CategoryCard from './Products/CategoryCard';
import Loader from './Loader';

const COLOR_CONFIGS = [
  { grad: 'from-emerald-400 to-teal-500', shadow: 'hover:shadow-emerald-300/50', activeShadow: 'shadow-emerald-500/40', iconBg: 'bg-emerald-50', iconText: 'text-emerald-600' },
  { grad: 'from-orange-400 to-amber-500', shadow: 'hover:shadow-orange-300/50', activeShadow: 'shadow-orange-500/40', iconBg: 'bg-orange-50', iconText: 'text-orange-600' },
  { grad: 'from-violet-500 to-purple-600', shadow: 'hover:shadow-violet-300/50', activeShadow: 'shadow-violet-500/40', iconBg: 'bg-violet-50', iconText: 'text-violet-600' },
  { grad: 'from-rose-400 to-pink-500', shadow: 'hover:shadow-rose-300/50', activeShadow: 'shadow-rose-500/40', iconBg: 'bg-rose-50', iconText: 'text-rose-600' },
  { grad: 'from-blue-400 to-indigo-500', shadow: 'hover:shadow-blue-300/50', activeShadow: 'shadow-blue-500/40', iconBg: 'bg-blue-50', iconText: 'text-blue-600' },
  { grad: 'from-amber-400 to-yellow-500', shadow: 'hover:shadow-amber-300/50', activeShadow: 'shadow-amber-500/40', iconBg: 'bg-amber-50', iconText: 'text-amber-600' },
  { grad: 'from-cyan-400 to-sky-500', shadow: 'hover:shadow-cyan-300/50', activeShadow: 'shadow-cyan-500/40', iconBg: 'bg-cyan-50', iconText: 'text-cyan-600' },
  { grad: 'from-lime-400 to-green-500', shadow: 'hover:shadow-lime-300/50', activeShadow: 'shadow-lime-500/40', iconBg: 'bg-lime-50', iconText: 'text-lime-600' },
];

const SkeletonCard = () => (
  <div className="animate-pulse rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
    <div className="aspect-square bg-gray-200/80 w-full" />
    <div className="px-2 pb-3 pt-2">
      <div className="mx-auto h-4 w-16 rounded-full bg-gray-200/80" />
    </div>
  </div>
);

const Categories = () => {
  const {
    categories,
    fetchCategories,
    setSelectedCategory,
    selectedCategory,
    categories_loading,
  } = useStore();
  const navigate = useNavigate();

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleClick = (categoryId) => {
    setSelectedCategory(categoryId);
    navigate(`/products/${categoryId}`);
  };

  return (
    <section className="relative overflow-hidden bg-white py-5 sm:py-6">
      {/* Soft background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-0 h-[500px] w-[500px] rounded-full bg-primary-100/30 blur-3xl" />
        <div className="absolute -right-1/4 bottom-0 h-[400px] w-[400px] rounded-full bg-amber-100/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-primary-600 border border-primary-100">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" />
            Shop by Category
          </span>

        </div>

        {/* Grid */}
        {categories_loading ? (
          <>
            <Loader compact label="Loading categories" sublabel="Finding the latest product categories." />
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 sm:gap-5">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 sm:gap-5">
            {categories.map((category, index) => (
              <CategoryCard
                key={category.category_id}
                category={category}
                isActive={String(selectedCategory) === String(category.category_id)}
                colorConfig={COLOR_CONFIGS[index % COLOR_CONFIGS.length]}
                onClick={handleClick}
              />
            ))}
          </div>
        )}

        {/* View All */}
        <div className="mt-6 text-center">
          <button
            onClick={() => { setSelectedCategory('all'); navigate('/products'); }}
            className="group inline-flex items-center gap-3 rounded-full bg-gray-900 px-8 py-4 text-sm font-bold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:bg-gray-800 hover:shadow-2xl"
          >
            Explore All Products
            <svg className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Categories;
